import React from 'react';
import { Link } from 'gatsby';
import { motion } from 'framer-motion';

// Hooks
import useLockBodyScroll from '../../../hooks/useLockBodyScroll/useLockBodyScroll';
import useMobileNavbarMenuAnimation from './useMobileNavbarMenuAnimation';
import { useVisitId } from '../../../hooks/useVisitId';
import useContextToken from '../../../hooks/useContextToken';

// Components
import { ButtonLink } from '../../common';

// Helpers
import { getBuyflowLink } from '../../../utils/helpers';

import './MobileNavbarMenu.scss';

// TEALIUM
import initTealiumTracker from 'tealium-tracker';
import { KiteButton, KiteIcon } from '@kite/react-kite';
import { login } from '../../../utils/auth/authHelpers';
import useAuthContext from '../../../hooks/useAuthContext';
let tealiumTrackEvent;
if (typeof window !== 'undefined') {
  const { trackEvent } = initTealiumTracker({ debugMode: true });
  tealiumTrackEvent = trackEvent;
}

interface IMobileNavbarMenu {
  isOpen: boolean;
  onClose: () => void;
  analytics: any;
}
export interface ILink {
  label: string;
  path: string;
  quantumLinkName?: string;
  icon?: string;
}

const links: ILink[] = [
  { label: 'Home', path: '/', quantumLinkName: 'home' },
  { label: 'Movies', path: '/movies/', quantumLinkName: 'movies' },
  { label: 'TV Shows', path: '/tv/', quantumLinkName: 'tvShows' },
  // { label: 'Events', path: '/events/', quantumLinkName: 'events' },
  { label: 'Networks', path: '/networks/', quantumLinkName: 'networks' },
  { label: 'Trailers', path: '/trailers/', quantumLinkName: 'trailers' },
  {
    label: 'TV Apps',
    path: '/tv-apps/',
    quantumLinkName: 'tvApps',
  },
  { label: 'Access', path: '/access/', quantumLinkName: 'access' },
];

const MobileNavbarMenu = ({
  isOpen,
  onClose,
  analytics,
}: IMobileNavbarMenu) => {
  // prevent scrolling behind menu when it is open
  useLockBodyScroll(isOpen);

  const { isValidSession, logout, loginInProgress } = useAuthContext();
  const { contextToken } = useContextToken();

  const {
    menuVariants,
    linksVariants,
    linkItemVariants,
    actionsVariants,
    actionItemVariants,
  } = useMobileNavbarMenuAnimation();

  // closes the menu if clicking on a menu item that is already active (gatsby does nothing by default)
  const url = typeof window !== 'undefined' ? window.location.pathname : '';
  const handleLinkClick = (link: ILink, url: string) => {
    // quantum tagging
    analytics.track('SpectrumOnDemand_selectAction_navigationClick', {
      currPageElemStdName: link.quantumLinkName,
      currPageElemUiName: link.quantumLinkName,
    });

    tealiumTrackEvent({
      linktype: 'header',
      linkname: link.label,
    });

    if (link.path === url) {
      onClose();
    }
  };

  const visitId = useVisitId();

  const buyflowLink = getBuyflowLink(
    'https://www.spectrum.com/cable-tv/plans?cmp=sspp_sod_gm_hphero_unauth_050523',
    'https://www.spectrum.net/change?cid=sspp_sod_gm_hphero_auth_050523'
  );

  return (
    <motion.div
      variants={menuVariants}
      className={`mobile-nav__menu ${isOpen ? '' : 'mobile-nav__menu--closed'}`}
      aria-hidden={!isOpen}
    >
      <motion.ul variants={linksVariants} className="links">
        {links.map((link) => (
          <motion.li variants={linkItemVariants} key={link.label}>
            <Link
              className="link"
              to={`${link.path}`}
              activeClassName="link--active"
              tabIndex={isOpen ? 0 : -1}
              onClick={() => handleLinkClick(link, url)}
              data-linktype="header"
              data-linkname={link.label}
            >
              {link.label}
              {link.icon && <KiteIcon icon={link.icon} />}
            </Link>
          </motion.li>
        ))}
      </motion.ul>
      <motion.ul variants={actionsVariants} className="actions">
        <motion.li variants={actionItemVariants}>
          <ButtonLink
            type="primary"
            path={`${buyflowLink}&visitId=${visitId}${contextToken}`}
            tabIndex={isOpen ? 0 : -1}
            onClick={() =>
              analytics.track('SpectrumOnDemand_selectAction_shop')
            }
            tealiumConfig={{
              dataLinkName: 'Shop',
              dataLinkType: 'header',
            }}
          >
            Shop
          </ButtonLink>
        </motion.li>
        <motion.li variants={actionItemVariants}>
          {!isValidSession ? (
            <KiteButton
              className="sign-in"
              variant="secondary"
              legacy
              onClick={() => login(contextToken, visitId)}
              loading={loginInProgress}
            >
              Sign In
            </KiteButton>
          ) : (
            <KiteButton
              className="sign-in"
              variant="secondary"
              legacy
              onClick={() => logout()}
              loading={loginInProgress}
            >
              Sign Out
            </KiteButton>
          )}
        </motion.li>
        <motion.li variants={actionItemVariants}>
          <ButtonLink
            className="live-tv-link"
            type="secondary"
            path={`https://watch.spectrum.net/livetv?visitId=${visitId}${contextToken}`}
            tabIndex={isOpen ? 0 : -1}
            onClick={() => {
              analytics.track('selectAction', {
                currPageElemStdName: 'MobileNavbarMenu__live-tv',
                msgCategory: 'navigation',
                msgTriggeredBy: 'user',
                opType: 'buttonClick',
              });
            }}
            tealiumConfig={{
              dataLinkName: 'Live TV',
              dataLinkType: 'header',
            }}
          >
            Live TV
          </ButtonLink>
        </motion.li>
      </motion.ul>
    </motion.div>
  );
};

export default MobileNavbarMenu;
